import * as React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
	Menu,
	MenuDivider,
	MenuIconContainer,
	MenuItem,
	MenuTag,
	TagTypeStyle,
} from "#components/primitive";
import { useBreadcrumb } from "#structure/providers/breadcrumb";
import RootStore from "#stores/index";
import { useLanguage } from "#structure/providers/language";
import { useWindowSize } from "#resources/hooks";
import { ChangeLanguageModal } from "../change-language-modal";
import Icon, { IconName } from "../../icon";
import s from "./nav-bar.scss";
import ChangeLanguageIcon from "./icons/change-language.svg";
import LogoutIcon from "./icons/logout.svg";

// Import nav-bar icons
import AccountStatementIcon from "./icons/account-statement.svg";
import AuditsIcon from "./icons/audits.svg";
import BagIcon from "./icons/bag-icon.svg";
import BarReportIcon from "./icons/bar-report.svg";
import BarsIcon from "./icons/bars.svg";
import BookingsIcon from "./icons/bookings.svg";
import BusinessIntelligenceIcon from "./icons/business-inteligence.svg";
import BusinessIntelligenceV2Icon from "./icons/business-intelligence-v2.svg";
import CargoCartIcon from "./icons/cargo-cart.svg";
import CashFlowIcon from "./icons/cash-flow.svg";
import ChartOfAccountsIcon from "./icons/chart-of-accounts.svg";
import ChartOfAccountsV2Icon from "./icons/chart-of-accounts-v2.svg";
import ClientsIcon from "./icons/clients.svg";
import CommunicationIcon from "./icons/communication.svg";
import ConfigAdmIcon from "./icons/config-adm.svg";
import ContractIcon from "./icons/contract.svg";
import CrmIcon from "./icons/crm.svg";
import CustomersIcon from "./icons/customers.svg";
import DashboardChartIcon from "./icons/dashboard-chart.svg";
import DeliveryIcon from "./icons/delivery.svg";
import DeliveryPersonWhiteIcon from "./icons/delivery-person-white.svg";
import DevicesIcon from "./icons/devices.svg";
import EmployeeIcon from "./icons/employee.svg";
import EventsIcon from "./icons/events.svg";
import FileCheckIcon from "./icons/file-check.svg";
import FinancialIcon from "./icons/financial.svg";
import FinancialReportIcon from "./icons/financial-report.svg";
import FiscalIcon from "./icons/fiscal.svg";
import GiftbackIcon from "./icons/giftback.svg";
import HelpIcon from "./icons/help.svg";
import KdsIcon from "./icons/kds.svg";
import ListMultipleIcon from "./icons/list-multiple.svg";
import LoyaltyIcon from "./icons/loyalty.svg";
import MegaphoneIcon from "./icons/megaphone.svg";
import MultivendorIcon from "./icons/multivendor.svg";
import MultivendorV2Icon from "./icons/multivendor-v2.svg";
import NewStorageIcon from "./icons/new-storage.svg";
import OperationIcon from "./icons/operation.svg";
import PackagesIcon from "./icons/packages.svg";
import PanelIcon from "./icons/panel.svg";
import PasswordCardIcon from "./icons/password-card.svg";
import PdvStatusIcon from "./icons/pdv-status.svg";
import PlaceIcon from "./icons/place.svg";
import PosMachineIcon from "./icons/pos-machine.svg";
import PreChargeIcon from "./icons/pre-charge.svg";
import ProductsIcon from "./icons/products.svg";
import PromotionsIcon from "./icons/promotions.svg";
import ReceptiveIcon from "./icons/receptive.svg";
import RechargeManagementIcon from "./icons/recharge-management.svg";
import ReportIcon from "./icons/report.svg";
import ReportsIcon from "./icons/reports.svg";
import ReserveIcon from "./icons/reserve.svg";
import SatisfactionSurveyIcon from "./icons/satisfaction-survey.svg";
import SellWithNoCardIcon from "./icons/sell-with-no-card.svg";
import SettingsIcon from "./icons/settings.svg";
import StockInventoryIcon from "./icons/stock-inventory.svg";
import StorageReportsIcon from "./icons/storage-reports.svg";
import TableConfigIcon from "./icons/table-config.svg";
import TableMapIcon from "./icons/table-map.svg";
import TableReportIcon from "./icons/table-report.svg";
import TaxIcon from "./icons/tax.svg";
import TaxDataIcon from "./icons/tax-data.svg";
import TeamIcon from "./icons/team.svg";
import TicketsIcon from "./icons/tickets.svg";
import TicketsV2Icon from "./icons/tickets-v2.svg";
import TicketsReportIcon from "./icons/tickets-report.svg";
import UsersIcon from "./icons/users.svg";
import WhatsUpIcon from "./icons/whats-up.svg";
import ZigAccountIcon from "./icons/zig-account.svg";
import ZigTagIcon from "./icons/zig-tag.svg";

export interface NavBarRoute {
	icon: string;
	label: string;
	url: string;
	tagType?: TagTypeStyle;
	readonly?: boolean;
}

interface INavBarProps {
	items: NavBarRoute[];
	type?: string;
	isOpenSandwichMenu: boolean;
	onChangeIsOpenSandwichMenu(isOpen: boolean): void;
}

export default observer(NavBar);

function LogoContainer({ children }: HTMLElement) {
	return <Link to={"/"}>{children}</Link>;
}

function NavBar({ isOpenSandwichMenu, items, onChangeIsOpenSandwichMenu }: INavBarProps) {
	const [openLanguageModal, setOpenLanguageModal] = React.useState(false);
	const { changeLanguage, languageInfo } = useLanguage();
	const { onChangeNavBarList, onChangeCurrentPage } = useBreadcrumb();
	const { isExtraSmallDevice } = useWindowSize();
	const { authStore } = React.useContext(RootStore);
	const { t } = useTranslation("nav", { keyPrefix: "navbar.default" });
	const { t: tGlobalKeys } = useTranslation("globalKeys");

	React.useEffect(() => {
		onChangeNavBarList(items);
	}, [items]);

	function onClick(item: NavBarRoute) {
		onChangeCurrentPage(item.label);

		if (onChangeIsOpenSandwichMenu && isExtraSmallDevice) {
			onChangeIsOpenSandwichMenu && onChangeIsOpenSandwichMenu(!isOpenSandwichMenu);
		}
	}

	// Icon mapping helper
	const getNavIcon = (iconName: string) => {
		const iconMap: Record<string, React.ComponentType> = {
			"account-statement": AccountStatementIcon,
			audits: AuditsIcon,
			"bag-icon": BagIcon,
			"bar-report": BarReportIcon,
			bars: BarsIcon,
			bookings: BookingsIcon,
			"business-inteligence": BusinessIntelligenceIcon,
			"business-intelligence-v2": BusinessIntelligenceV2Icon,
			"cargo-cart": CargoCartIcon,
			"cash-flow": CashFlowIcon,
			"change-language": ChangeLanguageIcon,
			"chart-of-accounts": ChartOfAccountsIcon,
			"chart-of-accounts-v2": ChartOfAccountsV2Icon,
			clients: ClientsIcon,
			communication: CommunicationIcon,
			"config-adm": ConfigAdmIcon,
			contract: ContractIcon,
			crm: CrmIcon,
			customers: CustomersIcon,
			"dashboard-chart": DashboardChartIcon,
			delivery: DeliveryIcon,
			"delivery-person-white": DeliveryPersonWhiteIcon,
			devices: DevicesIcon,
			employee: EmployeeIcon,
			events: EventsIcon,
			"file-check": FileCheckIcon,
			financial: FinancialIcon,
			"financial-report": FinancialReportIcon,
			fiscal: FiscalIcon,
			giftback: GiftbackIcon,
			help: HelpIcon,
			kds: KdsIcon,
			"list-multiple": ListMultipleIcon,
			logout: LogoutIcon,
			loyalty: LoyaltyIcon,
			megaphone: MegaphoneIcon,
			multivendor: MultivendorIcon,
			"multivendor-v2": MultivendorV2Icon,
			"new-storage": NewStorageIcon,
			operation: OperationIcon,
			packages: PackagesIcon,
			panel: PanelIcon,
			"password-card": PasswordCardIcon,
			"pdv-status": PdvStatusIcon,
			place: PlaceIcon,
			"pos-machine": PosMachineIcon,
			"pre-charge": PreChargeIcon,
			products: ProductsIcon,
			promotions: PromotionsIcon,
			receptive: ReceptiveIcon,
			"recharge-management": RechargeManagementIcon,
			report: ReportIcon,
			reports: ReportsIcon,
			reserve: ReserveIcon,
			"satisfaction-survey": SatisfactionSurveyIcon,
			"sell-with-no-card": SellWithNoCardIcon,
			settings: SettingsIcon,
			"stock-inventory": StockInventoryIcon,
			"storage-reports": StorageReportsIcon,
			"table-config": TableConfigIcon,
			"table-map": TableMapIcon,
			"table-report": TableReportIcon,
			tax: TaxIcon,
			"tax-data": TaxDataIcon,
			team: TeamIcon,
			tickets: TicketsIcon,
			"tickets-v2": TicketsV2Icon,
			"tickets-report": TicketsReportIcon,
			users: UsersIcon,
			"whats-up": WhatsUpIcon,
			"zig-account": ZigAccountIcon,
			"zig-tag": ZigTagIcon,
		};

		return iconMap[iconName] || null;
	};

	const menuItems = (items || []).map((item, index) => {
		const Icon = getNavIcon(item.icon);

		const content = (
			<>
				{Icon && (
					<MenuIconContainer>
						<Icon />
					</MenuIconContainer>
				)}
				<div>
					{item.label}
					{item.tagType ? (
						<MenuTag type={item.tagType}>
							{tGlobalKeys($ => {
								const tagType = item.tagType!;
								return $[tagType];
							})}
						</MenuTag>
					) : null}
				</div>
			</>
		);

		return (
			<MenuItem
				key={`navbar-item-${index}-${item.url}`}
				active={window.location.pathname === item.url}
				tagType={item.tagType}
			>
				{Boolean(item.readonly) ? (
					content
				) : (
					<Link className={s.item} to={item.url} onClick={() => onClick(item)}>
						{content}
					</Link>
				)}
			</MenuItem>
		);
	});

	menuItems.push(<MenuDivider key="divider" />);
	menuItems.push(
		<MenuItem key="change-language">
			<button className={s.item} onClick={() => setOpenLanguageModal(true)}>
				<MenuIconContainer className={s.changeLanguageIcon}>
					<ChangeLanguageIcon />
					{languageInfo?.icon && (
						<div className={s.languageFlag}>
							<Icon icon={languageInfo?.icon as IconName} />
						</div>
					)}
				</MenuIconContainer>
				<div>{t($ => $.changeLanguage)}</div>
			</button>
		</MenuItem>,
	);
	menuItems.push(
		<MenuItem key="logout">
			<button className={s.item} onClick={() => authStore.logout()}>
				<MenuIconContainer>
					<LogoutIcon />
				</MenuIconContainer>
				<div>{t($ => $.logout)}</div>
			</button>
		</MenuItem>,
	);

	return (
		<>
			<Menu LogoContainer={LogoContainer} isForceOpen={isOpenSandwichMenu}>
				{menuItems}
			</Menu>
			<ChangeLanguageModal
				changeLanguage={changeLanguage}
				isLanguageModalOpen={openLanguageModal}
				onChangeLanguageModalStatus={setOpenLanguageModal}
			/>
		</>
	);
}
